import { useWindowVirtualizer } from "@tanstack/react-virtual";
import moment from "moment/min/moment-with-locales";
import { useCallback, useMemo, useRef, useState } from "react";
import DataDescription from "../DataDescription";
import EmptyRow from "../EmptyRow";
import { DashboardPanel, SearchWithDownloadRow, SoleDescriptionRow, VirtualizedWindowContainer } from "../pageSections";
import useSortableData from "../useSortableData";
import ScheduleCheckSongsHeader from "./ScheduleCheckSongsHeader";
import ScheduleCheckSongsRow from "./ScheduleCheckSongsRow";

export default function ScheduleCheckSongsTable({ data, submittedFormData }) {
    const [filterData, setFilterData] = useState({ searchString: "" });

    const tableContainerRef = useRef(null);

    const songsMemo = useMemo(() => {
        if (data) {
            let filteredData = data;
            if (filterData.searchString) {
                filteredData = filteredData.filter(
                    x =>
                        x.artist.toLowerCase().includes(filterData.searchString.toLowerCase().trim()) ||
                        x.title.toLowerCase().includes(filterData.searchString.toLowerCase().trim()) ||
                        moment(x.played_at).format("L").includes(filterData.searchString.trim())
                );
            }
            return filteredData;
        }
    }, [data, filterData.searchString]);

    const { sortedItems, requestSort, sortConfig } = useSortableData(songsMemo);

    const csvHeaders = ["Artist", "Title", "Scheduled at", "Played at"];
    const csvData = useMemo(
        () =>
            sortedItems?.map(x => [
                x.artist,
                x.title,
                x.scheduled_start ? moment(x.scheduled_start)?.format("LTS") : "-",
                x.logged_start ? moment(x.logged_start).format("LTS") : "-",
            ]),
        [sortedItems]
    );

    const virtualizer = useWindowVirtualizer({
        count: sortedItems?.length || 1,
        estimateSize: useCallback(() => 32, []),
        overscan: 30,
        scrollMargin: tableContainerRef.current?.offsetTop ?? 0,
    });

    return (
        <DashboardPanel>
            <DashboardPanel.Title>Schedule</DashboardPanel.Title>
            <div className=" px-4">
                <SoleDescriptionRow>
                    <DataDescription>
                        <DataDescription.Station radioId={submittedFormData.station[0].radio_id} />
                        <DataDescription.Date date={submittedFormData.date} />
                        <DataDescription.HourRange startHour={submittedFormData.hourRange[0]} endHour={submittedFormData.hourRange[1]} />
                    </DataDescription>
                </SoleDescriptionRow>
                <SearchWithDownloadRow
                    filterData={filterData}
                    setFilterData={setFilterData}
                    csvHeaders={csvHeaders}
                    data={csvData}
                    filename={"Schedule Check"}
                />
            </div>
            <DashboardPanel.DataContent>
                <ScheduleCheckSongsHeader requestSort={requestSort} sortConfig={sortConfig} />
                <VirtualizedWindowContainer containerRef={tableContainerRef} virtualizer={virtualizer}>
                    {virtualizer.getVirtualItems().map(virtualRow => {
                        const item = sortedItems[virtualRow.index];
                        if (!item) return <EmptyRow key={virtualRow.key} text="No songs" />;
                        return <ScheduleCheckSongsRow key={virtualRow.key} item={item} virtualRow={virtualRow} virtualizer={virtualizer} />;
                    })}
                </VirtualizedWindowContainer>
            </DashboardPanel.DataContent>
        </DashboardPanel>
    );
}
